'use client';

import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import { AnimatePresence, motion, Transition } from 'framer-motion';

/** Every item is a small MUI Fab, so its height is known and can be collapsed in-flow on exit. */
const ITEM_HEIGHT_PX = 40;
const ITEM_GAP_PX = 8;

type FloatingActionsSlot = {
  element: HTMLDivElement | null;
  setElement: (el: HTMLDivElement | null) => void;
};

const FloatingActionsSlotContext = createContext<FloatingActionsSlot | null>(
  null
);

/**
 * Holds the portal target that lets page-level components (see `FloatingActionPortal`)
 * add their own button to the bottom-right stack. Wrap the layout with it once.
 */
export const FloatingActionsProvider = ({ children }: PropsWithChildren) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);

  return (
    <FloatingActionsSlotContext.Provider value={{ element, setElement }}>
      {children}
    </FloatingActionsSlotContext.Provider>
  );
};

const useFloatingActionsSlot = () => {
  const ctx = useContext(FloatingActionsSlotContext);
  if (!ctx) {
    throw new Error(
      'FloatingActions components must be rendered inside FloatingActionsProvider'
    );
  }
  return ctx;
};

/**
 * The bottom-right corner of the app. Children must be `FloatingAction` items with stable keys;
 * they stack bottom to top in DOM order, and the stack reflows smoothly when one leaves.
 * Anything sent through `FloatingActionPortal` sits on top of them.
 */
export const FloatingActionsStack = ({ children }: PropsWithChildren) => {
  const { setElement } = useFloatingActionsSlot();

  return (
    <Box
      position="fixed"
      bottom={16}
      right={24}
      zIndex={1000}
      display="flex"
      flexDirection="column-reverse"
      alignItems="flex-end"
    >
      <AnimatePresence>{children}</AnimatePresence>
      <div ref={setElement} />
    </Box>
  );
};

const popIn: Transition = { type: 'spring', stiffness: 600, damping: 30 };
const nudgeIn: Transition = { type: 'spring', stiffness: 500, damping: 18 };
const collapse: Transition = { duration: 0.2, ease: 'easeInOut' };
const popOut: Transition = { duration: 0.15, ease: 'easeIn' };

type FloatingActionProps = PropsWithChildren<{
  /** Overshoot on enter to grab attention. Off by default. */
  nudge?: boolean;
}>;

/**
 * One button in the stack. Scales in on mount and out on unmount while its slot
 * collapses, so the siblings slide over through normal flow with no layout measuring.
 */
export const FloatingAction = ({ nudge, children }: FloatingActionProps) => (
  <motion.div
    initial={{ height: 0, marginTop: 0, scale: 0, opacity: 0 }}
    animate={{
      height: ITEM_HEIGHT_PX,
      marginTop: ITEM_GAP_PX,
      scale: 1,
      opacity: 1,
      transition: {
        height: collapse,
        marginTop: collapse,
        scale: nudge ? nudgeIn : popIn,
        opacity: nudge ? nudgeIn : popIn
      }
    }}
    exit={{
      height: 0,
      marginTop: 0,
      scale: 0,
      opacity: 0,
      transition: {
        height: collapse,
        marginTop: collapse,
        scale: popOut,
        opacity: popOut
      }
    }}
  >
    {children}
  </motion.div>
);

/**
 * Renders its children as the top-most item of the stack from anywhere below
 * `FloatingActionsProvider`. Use it for buttons that belong to one page only.
 */
export const FloatingActionPortal = ({ children }: PropsWithChildren) => {
  const { element } = useFloatingActionsSlot();

  if (!element) {
    return null;
  }

  return createPortal(<FloatingAction>{children}</FloatingAction>, element);
};
